/* eslint-disable indent */
/* eslint-disable react-hooks/exhaustive-deps */
import { Modal, Form, Button, Space, message, App } from "antd";
import axios from "axios";
import { useEffect, useState, useRef } from "react";

import { AuthorModal } from "./AuthorModal";
import { FormStep1 } from "./FormStep1";
import { FormStep2 } from "./FormStep2";
import { createSlug } from "../../../../../utils/helper.jsx";
import { showErrorNotification } from "../../../../../utils/notification";
import { BLOG_PUBLISH_STATUS, MODAL_CONFIG_MODES } from "../../BlogsDashboard";

const BlogModal = ({
  config,
  onClose,
  loadBlogTree,
  ROOT_URL,
  BASE_URL,
  blogAuthors = {},
  refreshBlogAuthors,
  basicBlogDetailsList
}) => {
  // The static Modal.confirm() renders outside the React tree, so it never picks up the dark algorithm
  // from ConfigProvider. The instance from App.useApp() is context-aware.
  const { modal } = App.useApp();
  const [formStep1] = Form.useForm();
  const [formStep2] = Form.useForm();
  const [submittingStep1, setSubmittingStep1] = useState(false);
  const [submittingStep2, setSubmittingStep2] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isEdit, setIsEdit] = useState(config.mode === MODAL_CONFIG_MODES.EDIT_BLOG);
  const [parentActiveUniqueUrlIdentifier, setParentActiveUniqueUrlIdentifier] = useState(
    config?.parentNode?.activeUniqueUrlIdentifier || null
  );

  // Author Modal
  const [authorForm] = Form.useForm();
  const [authorModalOpen, setAuthorModalOpen] = useState(false);
  const [authorsLoading, _setAuthorsLoading] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [isUploadingProfilePicture, setIsUploadingProfilePicture] = useState(false);
  const [profilePictureFileMetadataId, setProfilePictureFileMetadataId] = useState(null);

  // Assets
  const [assets, setAssets] = useState([]);
  const [uploadingAsset, setUploadingAsset] = useState(false);

  const slugTouchedRef = useRef(false);

  useEffect(() => {
    setIsEdit(config.mode === MODAL_CONFIG_MODES.EDIT_BLOG);
    // If editing, fetch full blog payload and set to both forms and assets
    if (config.mode === MODAL_CONFIG_MODES.EDIT_BLOG) {
      const urlIdentifier = parentActiveUniqueUrlIdentifier;
      if (urlIdentifier) {
        axios
          .get(`${ROOT_URL}/${BASE_URL}/blogs/fetch`, { params: { urlIdentifier } })
          .then(({ data }) => {
            const {
              title,
              slug,
              displayOrderUnderParent,
              summary,
              readMinutes,
              authorIds,
              tags,
              draftContent,
              problems,
              resources,
              questions,
              publishStatus,
              isNewBlog
            } = data?.blog || {};
            const { previousBlogMetadata, nextBlogMetadata } = data || {};

            formStep1.setFieldsValue({
              title,
              slug,
              displayOrderUnderParent,
              summary,
              readMinutes,
              authorIds,
              tags,
              publishStatus,
              originalPublishStatus: publishStatus,
              previousBlogIdentifier: previousBlogMetadata?.activeUniqueUrlIdentifier || null,
              nextBlogIdentifier: nextBlogMetadata?.activeUniqueUrlIdentifier || null
            });
            formStep2.setFieldsValue({
              draftContent,
              problems,
              resources,
              questions,
              isNewBlog
            });

            slugTouchedRef.current = true;
          })
          .catch(showErrorNotification);
      }
    } else {
      formStep1.setFieldsValue({
        readMinutes: 10,
        tags: [],
        authorIds: []
      });
      formStep2.setFieldsValue({
        draftContent: "",
        problems: [],
        resources: [],
        questions: []
      });
    }
  }, [config.open]);

  useEffect(() => {
    const onTitleChange = ({ title }) => {
      if (!title || slugTouchedRef.current) return;
      formStep1.setFieldsValue({ slug: createSlug(title) });
    };

    const unsubscribe = formStep1.subscribe
      ? formStep1.subscribe(({ values }) => onTitleChange(values))
      : null;

    return () => {
      if (unsubscribe && typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [formStep1]);

  const handleClose = () => {
    modal.confirm({
      title: "Discard changes?",
      content: "Are you sure you want to close? Unsaved changes will be lost.",
      okText: "Yes, close",
      cancelText: "No, keep editing",
      onOk() {
        formStep1.resetFields();
        formStep2.resetFields();
        setAssets([]);
        onClose(false);
      }
    });
  };

  const handleSaveStep1 = async (values) => {
    const { previousBlogIdentifier = null, nextBlogIdentifier = null } = values;

    const isLinkedBlogSelfReferencing =
      isEdit &&
      parentActiveUniqueUrlIdentifier !== null &&
      (parentActiveUniqueUrlIdentifier === previousBlogIdentifier ||
        parentActiveUniqueUrlIdentifier === nextBlogIdentifier);
    if (isLinkedBlogSelfReferencing) {
      message.error("A blog cannot reference itself as previous or next blog.");
      return;
    }
    setSubmittingStep1(true);
    try {
      const step1Response = await axios.post(
        `${ROOT_URL}/${BASE_URL}/blogs/${isEdit ? "update" : "create"}`,
        {
          ...values,
          previousBlogIdentifier,
          nextBlogIdentifier,
          activeUniqueUrlIdentifier: isEdit ? parentActiveUniqueUrlIdentifier : null, // for create, we pass null to active url identifier
          parentActiveUniqueUrlIdentifier: isEdit
            ? parentActiveUniqueUrlIdentifier
            : config?.parentNode?.activeUniqueUrlIdentifier
        }
      );
      const data = step1Response?.data;
      message.success(isEdit ? "Blog updated successfully" : "Blog created successfully");
      setParentActiveUniqueUrlIdentifier(
        data?.activeUniqueUrlIdentifier || parentActiveUniqueUrlIdentifier
      );
      setIsEdit(true);
      setCurrentStep(2);
      loadBlogTree();
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setSubmittingStep1(false);
    }
  };

  const handlePublishBlog = async () => {
    try {
      if (!isEdit) {
        message.error("Please save the blog before publishing.");
        return;
      }

      await axios.post(`${ROOT_URL}/${BASE_URL}/blogs/publish`, {
        activeUniqueUrlIdentifier: parentActiveUniqueUrlIdentifier
      });

      message.success("Blog published successfully");

      formStep1.setFieldsValue({
        publishStatus: BLOG_PUBLISH_STATUS.PUBLISHED,
        originalPublishStatus: BLOG_PUBLISH_STATUS.PUBLISHED
      });
      loadBlogTree();
    } catch (err) {
      showErrorNotification(err);
    }
  };

  const handleSaveStep2 = async (values) => {
    setSubmittingStep2(true);
    try {
      await axios.post(`${ROOT_URL}/${BASE_URL}/blogs/${isEdit ? "update" : "create"}`, {
        ...values,
        activeUniqueUrlIdentifier: parentActiveUniqueUrlIdentifier
      });
      message.success("Blog saved successfully");
      loadBlogTree();
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setSubmittingStep2(false);
    }
  };

  const createBlogAuthor = async (blogAuthorData) => {
    try {
      await axios.post(`${ROOT_URL}/${BASE_URL}/authors/create`, {
        ...blogAuthorData,
        fileMetadataId: profilePictureFileMetadataId
      });
      message.success("Author added successfully");
      setAuthorModalOpen(false);
      authorForm.resetFields();
      setProfilePictureUrl(null);
      setProfilePictureFileMetadataId(null);
      await refreshBlogAuthors();
    } catch (err) {
      showErrorNotification(err);
    }
  };

  const uploadProfilePicture = async (file) => {
    setIsUploadingProfilePicture(true);
    try {
      const fd = new FormData();
      fd.append("file", file);

      const result = await axios.post(`${ROOT_URL}/${BASE_URL}/authors/upload-asset`, fd);
      const { blogAssetHostedUrl, fileMetadataId } = result.data;
      setProfilePictureUrl(blogAssetHostedUrl);
      setProfilePictureFileMetadataId(fileMetadataId);
      authorForm.setFieldsValue({ profilePictureUrl: blogAssetHostedUrl });
      message.success("Profile picture uploaded!");
      return false;
    } catch (err) {
      showErrorNotification(err);
      return false;
    } finally {
      setIsUploadingProfilePicture(false);
    }
  };

  const uploadAssets = async (files = []) => {
    if (!isEdit) {
      message.error("Please save Step 1 before uploading assets.");
      return [];
    }
    if (!files.length) {
      return [];
    }

    setUploadingAsset(true);

    try {
      const fileUploadPromises = files.map(async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("activeUniqueUrlIdentifier", parentActiveUniqueUrlIdentifier);

        try {
          const result = await axios.post(`${ROOT_URL}/${BASE_URL}/blogs/upload-asset`, fd);
          return {
            filename: file.name,
            url: result.data
          };
        } catch (err) {
          console.error("Upload failed:", file.name);
          return null;
        }
      });

      const results = await Promise.all(fileUploadPromises);
      const successfulUploads = results.filter(Boolean);
      if (successfulUploads.length) {
        setAssets((prev) => [...prev, ...successfulUploads]);
        message.success(
          `${successfulUploads.length} asset${successfulUploads.length > 1 ? "s" : ""} uploaded`
        );
      }

      const failedCount = results.length - successfulUploads.length;
      if (failedCount > 0) {
        message.warning(`${failedCount} asset${failedCount > 1 ? "s" : ""} failed`);
      }

      return successfulUploads;
    } catch (err) {
      showErrorNotification(err);
      return [];
    } finally {
      setUploadingAsset(false);
    }
  };

  const renderStepControls = () => {
    if (currentStep === 1) {
      return (
        <Space wrap style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={() => {
              formStep1
                .validateFields()
                .then((values) => handleSaveStep1(values))
                .catch(() => {
                  message.error("Please fix errors in Step 1.");
                });
            }}
            loading={submittingStep1}
          >
            Save Step 1 & Continue
          </Button>
        </Space>
      );
    }

    return (
      <Space wrap style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          onClick={() => {
            setCurrentStep(1);
          }}
        >
          Back
        </Button>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          type="primary"
          onClick={() => {
            formStep2
              .validateFields()
              .then((values) => handleSaveStep2(values))
              .catch(() => {
                message.error("Please fix errors in Step 2.");
              });
          }}
          loading={submittingStep2 || uploadingAsset}
        >
          Save Step 2
        </Button>
      </Space>
    );
  };

  return (
    <>
      <Modal
        open={config.open}
        title={
          isEdit
            ? `Edit Blog: ${config?.parentNode?.title || ""}`
            : `Add New Blog in "${config?.parentNode?.title || "Root"}"`
        }
        onCancel={handleClose}
        footer={null}
        width={1000}
        destroyOnClose={false}
      >
        <div style={{ marginBottom: 16 }}>
          <Button
            type={currentStep === 1 ? "primary" : "default"}
            onClick={() => setCurrentStep(1)}
            style={{ marginRight: 8 }}
          >
            Step 1 — Basic Info
          </Button>
          <Button
            type={currentStep === 2 ? "primary" : "default"}
            onClick={() => setCurrentStep(2)}
          >
            Step 2 — Content & Assets
          </Button>
        </div>

        {currentStep === 1 ? (
          <FormStep1
            formStep1={formStep1}
            handleSaveStep1={handleSaveStep1}
            handlePublishBlog={handlePublishBlog}
            slugTouchedRef={slugTouchedRef}
            authorsLoading={authorsLoading}
            authorsOptions={blogAuthors || {}}
            setAuthorModalOpen={setAuthorModalOpen}
            renderStepControls={renderStepControls}
            basicBlogDetailsList={basicBlogDetailsList}
          />
        ) : (
          <FormStep2
            formStep2={formStep2}
            handleSaveStep2={handleSaveStep2}
            uploadingAsset={uploadingAsset}
            assets={assets}
            uploadAssets={uploadAssets}
            renderStepControls={renderStepControls}
          />
        )}
      </Modal>
      <AuthorModal
        authorModalOpen={authorModalOpen}
        setAuthorModalOpen={setAuthorModalOpen}
        authorForm={authorForm}
        submitBlogAuthor={createBlogAuthor}
        uploadProfilePicture={uploadProfilePicture}
        isUploadingProfilePicture={isUploadingProfilePicture}
        profilePictureUrl={profilePictureUrl}
        referenceData={blogAuthors}
      />
    </>
  );
};

export default BlogModal;
